/*LIBRARY*/
import { useCallback, useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import bbox from "@turf/bbox";

/*COMPONENT*/
import LayerBasic from "../layer/LayerBasic";
import MARKER_GEO from "../layer/MARKER_GEO";
import CheckBoxNew from "../common/CheckBoxNew";

/*REDUX*/
import { set_value } from "../../actions/formActions";
import { set_layer_value } from "../../actions/layerActions";
import {
  get_location_list,
  set_value_properties,
} from "../../actions/propertiesActions";

/*PICTURE*/
import icon_pin from "../../img/icon_pin.svg";
import icon_pin_trans from "../../img/icon_pin_trans.svg";
import icon_full_screen_no from "../../img/icon_full_screen_no.svg";
import icon_full_screen_active from "../../img/icon_full_screen_active.svg";
import icon_location from "../../img/location.svg";

/*FUNCTION*/
import useLanguage from "../../hooks/useLanguage";
import is_lat_long_true from "../../validation/is_lat_long_true";

/*CONST*/
const basemap_url = process.env.REACT_APP_MAPID_BASEMAP;
const basemap_key = process.env.REACT_APP_BASEMAP_KEY;
const default_viewport = {
  latitude: -6.1784072398481555,

  longitude: 106.84251626772414,

  zoom: 7,
};

const markerStyle = {
  padding: "5px",
  color: "#fff",
  cursor: "pointer",
  background: "#0ca5eb",
  borderRadius: "50%",
  borderStyle: "solid",
  borderColor: "#fff",
  borderWidth: 2,
};

const RenderGeometryIcon = ({ type }) => {
  const iconStyle = { marginRight: "10px", flexShrink: 0 };
  const COLOR_PRIMARY = "#000000";
  const COLOR_SECONDARY = "#a1a1aa";
  const COLOR_FILL = "#f4f4f5";
  const strokeProps = {
    stroke: COLOR_PRIMARY,
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    fill: "none",
  };

  const isMulti = type && type.startsWith("Multi");

  if (type === "Point" || type === "MultiPoint") {
    return (
      <svg style={iconStyle} width="16" height="16" viewBox="0 0 24 24">
        {isMulti && <circle cx="18" cy="8" r="4" fill={COLOR_SECONDARY} />}
        <circle
          cx={isMulti ? "10" : "12"}
          cy={isMulti ? "14" : "12"}
          r={isMulti ? "5" : "6"}
          fill={COLOR_PRIMARY}
        />
      </svg>
    );
  }

  if (type === "LineString" || type === "MultiLineString") {
    return (
      <svg style={iconStyle} width="16" height="16" viewBox="0 0 24 24">
        {isMulti && (
          <path
            d="M7 20 L15 12 L23 4"
            stroke={COLOR_SECONDARY}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        )}
        <path
          d={isMulti ? "M3 20 L11 12 L19 4" : "M4 20 L12 12 L20 4"}
          {...strokeProps}
        />
      </svg>
    );
  }

  if (type === "Polygon" || type === "MultiPolygon") {
    return (
      <svg style={iconStyle} width="16" height="16" viewBox="0 0 24 24">
        {isMulti && (
          <path
            d="M10 6 L22 8 L20 18 L10 16 Z"
            stroke={COLOR_SECONDARY}
            strokeWidth="2"
            fill="none"
            strokeLinejoin="round"
          />
        )}

        <path
          d={
            isMulti
              ? "M4 10 L16 12 L14 22 L4 20 Z"
              : "M5 5 L19 7 L17 19 L3 17 Z"
          }
          fill={COLOR_FILL}
          stroke={COLOR_PRIMARY}
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return null;
};

const MapComponent = ({
  isFullMap,
  set_polygon_feature,
  handleFullMap,
  onGetLocation: propsOnGetLocation,
  link_step_3,
}) => {
  const dispatch = useDispatch();
  const { dict, language } = useLanguage();
  const { latitude, longitude } = useSelector((state) => state?.form);
  const { geo_layer_wo_geojson } = useSelector(
    (state) => state?.form,
    shallowEqual,
  );

  const { location_result_list, feature_geocode, location_status_gps } =
    useSelector((state) => state?.properties);
  const { map_object } = useSelector((state) => state?.layer);
  const map_object_ref = useRef(map_object);

  // 1. ADD: Ref to ignore the next useEffect update
  const ignoreNextFlyToRef = useRef(false);

  const [isSatellite, setIsSatellite] = useState(false);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [isLockCoordinate, setIsLockCoordinate] = useState(false);
  const [isPinAnimating, setIsPinAnimating] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const animationTimerRef = useRef(null);
  const markerGps = useRef(null);
  const searchTimeoutRef = useRef(null);
  const isLocationChangeable = geo_layer_wo_geojson.isLocationChangeable;
  const layer_attach = geo_layer_wo_geojson?.layer_form_list?.[0];

  useEffect(() => {
    if (!isLocationChangeable) {
      setIsLockCoordinate(true);
    }
  }, [isLocationChangeable]);

  const showPosition = useCallback(
    (position) => {
      const lat = Number(parseFloat(position.coords.latitude).toFixed(5));
      const long = Number(parseFloat(position.coords.longitude).toFixed(8));

      // We set this to true so the automatic useEffect listener doesn't fire
      // and override our specific "Zoom 15" logic below.
      ignoreNextFlyToRef.current = true;

      dispatch(set_value("latitude", lat));
      dispatch(set_value("longitude", long));
      dispatch(set_value("is_location_exist", true));
      dispatch(
        set_value_properties({ key: "location_status_gps", value: true }),
      );
      if (set_polygon_feature) {
        set_polygon_feature({ latitude: lat, longitude: long });
      }
      setLocationLoading(false);

      if (map_object_ref.current) {
        map_object_ref.current.flyTo({ center: [long, lat], zoom: 15 });
      }
    },
    [dispatch, set_polygon_feature],
  );

  const handleGetLocation = () => {
    setIsLockCoordinate(true);
    if (propsOnGetLocation) {
      propsOnGetLocation();
      return;
    }
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(showPosition, (error) => {
        console.error("Error getting location", error);
        alert("Gagal mendapatkan lokasi. Pastikan GPS aktif.");
        setLocationLoading(false);
      });
    } else {
      alert("Geolocation is not supported by this browser.");
      setLocationLoading(false);
    }
  };

  const set_lat_long = ({ latitude, longitude }) => {
    if (is_lat_long_true({ latitude, longitude })) {
      if (map_object_ref.current) {
        map_object_ref.current.flyTo({ center: [longitude, latitude] });
      }
    } else {
      if (map_object_ref.current) {
        map_object_ref.current.flyTo({
          center: [default_viewport.longitude, default_viewport.latitude],
        });
      }
    }
  };

  useEffect(() => {
    if (map_object_ref.current) {
      setTimeout(() => {
        map_object_ref.current.resize();
      }, 100);
    }
  }, [isFullMap]);

  const handleManualLatChange = (e) => {
    const val = e.target.value;
    dispatch(set_value("latitude", val));
  };

  const handleManualLongChange = (e) => {
    const val = e.target.value;
    dispatch(set_value("longitude", val));
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.trim().length > 2) {
      searchTimeoutRef.current = setTimeout(() => {
        const bounds = map_object_ref.current?.getBounds();
        let viewbox = "";
        if (bounds) {
          const sw = bounds.getSouthWest();
          const ne = bounds.getNorthEast();
          viewbox = `${sw.lng},${sw.lat},${ne.lng},${ne.lat}`;
        }
        dispatch(get_location_list({ search_input: value, viewbox }));
        setShowDropdown(true);
      }, 500);
    } else {
      setShowDropdown(false);
    }
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setShowDropdown(false);
  };

  const handleLocationSelect = (location) => {
    if (location.geojson) {
      dispatch({
        type: "set_value_properties",
        payload: { key: "feature_geocode", value: location.geojson },
      });
    }
    const lat = parseFloat(location.lat);
    const lon = parseFloat(location.lon);

    // 2. SET FLAG: Prevent the useEffect below from triggering a generic flyTo
    ignoreNextFlyToRef.current = true;

    dispatch(set_value("latitude", Number(lat.toFixed(5))));
    dispatch(set_value("longitude", Number(lon.toFixed(8))));
    dispatch(set_value("is_location_exist", true));
    setIsLockCoordinate(true);
    if (set_polygon_feature) {
      set_polygon_feature({ latitude: lat, longitude: lon });
    }
    setSearchInput(location.display_name);
    setShowDropdown(false);

    if (map_object_ref.current) {
      const type = location.geojson?.type;

      // 3. EXECUTE: Manual control over camera movement
      if (type === "Point" || type === "MultiPoint") {
        // Case 2: Point -> FlyTo Zoom 15
        map_object_ref.current.flyTo({ center: [lon, lat], zoom: 15 });
      } else if (location.geojson) {
        // Case 3: Geometry -> FitBounds
        const featureBbox = bbox(location.geojson);

        // Listen for the "moveend" event, which fires when fitBounds finishes
        map_object_ref.current.once("moveend", () => {
          if (map_object_ref.current) {
            // Fly to the coordinates without specifying zoom
            // This keeps the zoom level set by fitBounds
            map_object_ref.current.flyTo({
              center: [lon, lat],
            });
          }
        });

        map_object_ref.current.fitBounds(featureBbox, {
          padding: 100,
          maxZoom: 15,
        });
      } else {
        // Fallback
        map_object_ref.current.flyTo({ center: [lon, lat], zoom: 15 });
      }
    }
  };

  useEffect(() => {
    if (is_lat_long_true({ latitude, longitude }) && set_polygon_feature) {
      set_polygon_feature({ latitude, longitude });

      if (geo_layer_wo_geojson?.isLocationChangeable) {
        dispatch(set_value("is_location_exist", true));
      }
    }
  }, [
    dispatch,
    latitude,
    longitude,
    geo_layer_wo_geojson,
    set_polygon_feature,
  ]);

  // 4. CHECK FLAG: Only run generic set_lat_long if NOT ignored
  useEffect(() => {
    if (ignoreNextFlyToRef.current) {
      // Reset the flag and do nothing (let handleLocationSelect control the map)
      ignoreNextFlyToRef.current = false;
      return;
    }
    set_lat_long({ latitude, longitude });
  }, [latitude, longitude]);

  const handleToggleMap = () => {
    const nextState = !isSatellite;
    setIsSatellite(nextState);
    const street_style = `${basemap_url}/styles/street-2d-building/style.json?key=${basemap_key}`;
    const satellite_style = `${basemap_url}/styles/satellite/style.json?key=${basemap_key}`;
    let style_url = nextState ? satellite_style : street_style;
    if (map_object_ref.current) {
      map_object_ref.current.setStyle(style_url);
    }
  };

  const addMarker = () => {
    if (map_object_ref.current) {
      if (markerGps?.current) {
        markerGps.current?.remove();
        markerGps.current = null;
      }
      const markerElement = document.createElement("div");
      for (const item in markerStyle) {
        markerElement.style[item] = markerStyle[item];
      }
      const marker = new maplibregl.Marker({
        element: markerElement,
        draggable: isLocationChangeable ? true : false,
      })
        .setLngLat([longitude, latitude])
        .addTo(map_object_ref.current);
      markerGps.current = marker;
      marker.on("dragend", (e) => {
        const { lat, lng } = e.target?.getLngLat();
        dispatch(set_value("longitude", Number(lng.toFixed(8))));
        dispatch(set_value("latitude", Number(lat.toFixed(5))));
      });
    }
  };

  useEffect(addMarker, [dispatch, latitude, longitude, isLocationChangeable]);

  useEffect(() => {
    if (!map_object_ref.current || !isMapLoaded) return;
    const SOURCE_ID = "geocode-source";
    const LAYER_ID_FILL = "geocode-layer-fill";
    const LAYER_ID_LINE = "geocode-layer-line";
    const LAYER_ID_POINT = "geocode-layer-point";
    const removeExisting = () => {
      if (map_object_ref.current.getLayer(LAYER_ID_POINT))
        map_object_ref.current.removeLayer(LAYER_ID_POINT);
      if (map_object_ref.current.getLayer(LAYER_ID_LINE))
        map_object_ref.current.removeLayer(LAYER_ID_LINE);
      if (map_object_ref.current.getLayer(LAYER_ID_FILL))
        map_object_ref.current.removeLayer(LAYER_ID_FILL);
      if (map_object_ref.current.getSource(SOURCE_ID))
        map_object_ref.current.removeSource(SOURCE_ID);
    };
    removeExisting();
    if (!feature_geocode) return;
    map_object_ref.current.addSource(SOURCE_ID, {
      type: "geojson",
      data: feature_geocode,
    });
    const geometryType = feature_geocode.type;
    if (geometryType === "Point") {
      map_object_ref.current.addLayer({
        id: LAYER_ID_POINT,
        type: "circle",
        source: SOURCE_ID,
        paint: {
          "circle-radius": 8,
          "circle-color": "#0ca5eb",
          "circle-stroke-width": 2,
          "circle-stroke-color": "#ffffff",
        },
      });
    } else if (["LineString", "MultiLineString"].includes(geometryType)) {
      map_object_ref.current.addLayer({
        id: LAYER_ID_LINE,
        type: "line",
        source: SOURCE_ID,
        paint: { "line-color": "#0ca5eb", "line-width": 3 },
      });
    } else if (["Polygon", "MultiPolygon"].includes(geometryType)) {
      map_object_ref.current.addLayer({
        id: LAYER_ID_FILL,
        type: "fill",
        source: SOURCE_ID,
        paint: { "fill-color": "#0ca5eb", "fill-opacity": 0.3 },
      });
      map_object_ref.current.addLayer({
        id: LAYER_ID_LINE,
        type: "line",
        source: SOURCE_ID,
        paint: { "line-color": "#0ca5eb", "line-width": 2 },
      });
    }
    return () => removeExisting();
  }, [feature_geocode, isMapLoaded]);

  const setLatLongReducer = useCallback(() => {
    if (isLockCoordinate) return;
    const center = map_object_ref.current.getCenter();
    dispatch(set_value("longitude", Number(center.lng.toFixed(8))));
    dispatch(set_value("latitude", Number(center.lat.toFixed(5))));
    if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
    setIsPinAnimating(true);
    animationTimerRef.current = setTimeout(() => setIsPinAnimating(false), 400);
  }, [dispatch, isLockCoordinate]);

  useEffect(() => {
    if (map_object_ref.current) {
      if (isFullMap) map_object_ref.current.on("moveend", setLatLongReducer);
      else map_object_ref.current.off("moveend", setLatLongReducer);
    }
    return () => {
      if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (map_object_ref.current)
        map_object_ref.current.off("moveend", setLatLongReducer);
    };
  }, [isFullMap, setLatLongReducer]);

  useEffect(() => {
    const map_object = new maplibregl.Map({
      container: "map_object",
      style: `${basemap_url}/styles/street-2d-building/style.json?key=${basemap_key}`,
      center: [default_viewport?.longitude, default_viewport?.latitude],
      zoom: 7,
      maxPitch: 85,
      projection: "globe",
      attributionControl: false,
    });
    dispatch(set_layer_value("map_object", map_object));
    map_object_ref.current = map_object;
    map_object?.on("load", () => setIsMapLoaded(true));
    return () => map_object?.remove();
  }, [dispatch]);

  const containerStyle = {
    width: "100%",
    height: isFullMap ? "100%" : "500px",
    visibility: isMapLoaded ? "visible" : "hidden",
    position: isFullMap ? "fixed" : "relative",
    top: 0,
    left: 0,
    overflow: "hidden",
    zIndex: isFullMap ? 1000 : 1,
    borderRadius: isFullMap ? "0" : "12px",
    backgroundColor: "#fff",
  };

  const loadingStyle = {
    fontSize: "20px",
    height: isFullMap ? "100%" : "500px",
    width: "100%",
    zIndex: 4,
    visibility: isMapLoaded ? "hidden" : "visible",
    textAlign: "center",
    position: "absolute",
    top: 0,
    left: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f0f0f0",
    borderRadius: isFullMap ? "0" : "12px",
  };

  const controlPanelStyle = {
    position: "absolute",
    top: "10px",
    right: "10px",
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    padding: "8px",
    borderRadius: "8px",
    boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
    zIndex: 10,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    alignItems: "flex-start",
  };

  const buttonStyle = {
    width: "100%",
    padding: "5px",
    cursor: "pointer",
    backgroundColor: "#fff",
    border: "1px solid #ccc",
    borderRadius: "4px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };

  const latLongContainerStyle = {
    position: "absolute",
    bottom: isFullMap ? "80px" : "10px",
    left: "10px",
    zIndex: 10,
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  };

  const inputGroupStyle = {
    display: "flex",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    padding: "4px 8px",
    borderRadius: "4px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
    fontSize: "12px",
  };

  const labelStyle = {
    fontWeight: "bold",
    marginRight: "6px",
    minWidth: "30px",
    color: "#333",
  };

  const inputStyle = {
    border: "1px solid #ddd",
    padding: "3px",
    borderRadius: "4px",
    width: "100px",
    fontSize: "12px",
    backgroundColor: isLocationChangeable ? "#fff" : "#f5f5f5",
    color: "#000",
  };

  const centerPinStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -100%)",
    zIndex: 5,
    pointerEvents: "none",
    animation: isPinAnimating ? "bounce 0.4s ease-in-out" : "none",
  };

  const searchContainerStyle = {
    position: "absolute",
    top: "10px",
    left: "10px",
    right: isFullMap ? "220px" : "200px",
    maxWidth: isFullMap ? "500px" : "400px",
    zIndex: 100,
  };

  const searchWrapperStyle = {
    display: "flex",
    alignItems: "center",
    width: "100%",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: "11px",
    padding: "0 10px",
  };

  const searchInputStyle = {
    flex: 1,
    border: "none",
    outline: "none",
    padding: "0",
    margin: "0",
    fontSize: "14px",
    backgroundColor: "transparent",
    color: "#333",
  };

  const clearButtonStyle = {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#999",
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px",
    marginLeft: "5px",
  };

  const dropdownStyle = {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    marginTop: "4px",
    backgroundColor: "white",
    border: "1px solid #ddd",
    borderRadius: "4px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    maxHeight: isFullMap ? "300px" : "150px",
    overflowY: "auto",
    zIndex: 100,
  };

  const dropdownItemStyle = {
    padding: "10px 12px",
    cursor: "pointer",
    borderBottom: "1px solid #f0f0f0",
    fontSize: "13px",
    display: "flex",
    alignItems: "center",
  };

  const continueButtonStyle = {
    position: "fixed",
    bottom: "20px",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 50,
    padding: "12px 32px",
    fontSize: "16px",
    fontWeight: "bold",
    textDecoration: "none",
    borderRadius: "15px",
    cursor: "pointer",
    backgroundColor: "#000",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
  };

  const isNextDisabled = !isLocationChangeable && !location_status_gps;

  return (
    <section style={{ position: "relative", width: "100%" }}>
      <style>
        {`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .loading-spinner { border: 2px solid #f3f3f3; border-top: 2px solid #3498db; border-radius: 50%; width: 14px; height: 14px; animation: spin 1s linear infinite; }
        @keyframes bounce { 0% { transform: translate(-50%, -100%); } 50% { transform: translate(-50%, -150%); } 100% { transform: translate(-50%, -100%); } }
        .dropdown-item { transition: background-color 0.15s ease; }
        .dropdown-item:hover { background-color: #f5f5f5; }
        .dropdown-item:last-child { border-bottom: none; }
        `}
      </style>

      <div style={loadingStyle}>
        <b>{dict?.["Peta sedang dimuat"]?.[language]}...</b>
      </div>

      <div id="map_object" style={containerStyle}>
        {layer_attach?.geojson?.features?.length > 0 && (
          <>
            <LayerBasic geo_layer={layer_attach} />

            <MARKER_GEO geo_layer={layer_attach} />
          </>
        )}

        {isLocationChangeable && (
          <div style={searchContainerStyle}>
            <div style={searchWrapperStyle}>
              <input
                type="text"
                placeholder="Cari lokasi..."
                value={searchInput}
                onChange={handleSearchInputChange}
                style={searchInputStyle}
              />

              {searchInput && (
                <button
                  style={clearButtonStyle}
                  onClick={handleClearSearch}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>

            {showDropdown && location_result_list?.length > 0 && (
              <div style={dropdownStyle}>
                {location_result_list.map((location, index) => (
                  <div
                    key={`${location.place_id}-${index}`}
                    className="dropdown-item"
                    style={dropdownItemStyle}
                    onClick={() => handleLocationSelect(location)}
                  >
                    <RenderGeometryIcon type={location?.geojson?.type} />

                    <span
                      style={{
                        overflow: "hidden",

                        textOverflow: "ellipsis",

                        whiteSpace: "nowrap",
                      }}
                    >
                      {location.display_name}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={latLongContainerStyle}>
          <div style={inputGroupStyle}>
            <span style={labelStyle}>Lat</span>

            <input
              type="number"
              value={latitude}
              disabled={!isLocationChangeable}
              onChange={handleManualLatChange}
              style={inputStyle}
            />
          </div>

          <div style={inputGroupStyle}>
            <span style={labelStyle}>Long</span>

            <input
              type="number"
              value={longitude}
              disabled={!isLocationChangeable}
              onChange={handleManualLongChange}
              style={inputStyle}
            />
          </div>
        </div>

        <div style={controlPanelStyle}>
          <CheckBoxNew
            text="isSatellite"
            title="Citra Satelit"
            value={isSatellite || false}
            handle={handleToggleMap}
          />

          {isFullMap ? (
            <>
              <button
                onClick={handleGetLocation}
                disabled={locationLoading}
                style={buttonStyle}
                title="Get GPS Location"
              >
                {locationLoading ? (
                  <div className="loading-spinner"></div>
                ) : (
                  <img
                    src={icon_location}
                    alt="GPS"
                    style={{ width: "16px", height: "16px" }}
                  />
                )}
              </button>

              {isLocationChangeable && (
                <CheckBoxNew
                  text="isLockCoordinate"
                  title="Kunci titik"
                  value={isLockCoordinate || false}
                  handle={() => setIsLockCoordinate(!isLockCoordinate)}
                />
              )}

              <button onClick={handleFullMap} style={buttonStyle}>
                <img
                  src={icon_full_screen_no}
                  alt="Exit"
                  style={{ width: "16px", height: "16px" }}
                />
              </button>
            </>
          ) : (
            <button
              onClick={handleFullMap}
              style={{ ...buttonStyle, marginTop: "5px" }}
            >
              <img
                src={icon_full_screen_active}
                alt="Full"
                style={{ width: "16px", height: "16px" }}
              />
            </button>
          )}
        </div>

        {isFullMap && (
          <>
            <div style={centerPinStyle}>
              <img
                src={isLockCoordinate ? icon_pin_trans : icon_pin}
                alt="Center Pin"
                style={{ width: "40px", height: "40px" }}
              />
            </div>

            {isNextDisabled ? (
              <div
                style={{
                  ...continueButtonStyle,

                  backgroundColor: "#cccccc",

                  cursor: "not-allowed",

                  pointerEvents: "none",
                }}
              >
                {dict?.["Lanjut"]?.[language]}

                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />

                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </div>
            ) : (
              <Link
                className="button"
                to={link_step_3}
                style={continueButtonStyle}
              >
                {dict?.["Lanjut"]?.[language]}

                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />

                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </Link>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default MapComponent;
